import { useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';

type Role = 'admin' | 'hr' | 'payroll' | 'employee';

/**
 * Custom hook for role-based authorization
 * Uses the Microsoft 365 SSO user information to determine user roles
 */
export const useRole = () => {
  const { user } = useContext(AuthContext);

  /**
   * Check if the user has any of the specified roles
   * @param roles - Array of roles to check against
   * @returns boolean indicating if the user has any of the specified roles
   */
  const hasAnyRole = (roles: Role[]): boolean => {
    if (!user) return false;
    
    // Access roles from the user object
    const userRoles: string[] = user.roles || [];
    
    return roles.some(role => userRoles.includes(role));
  };

  /**
   * Check if the user has all of the specified roles
   * @param roles - Array of roles to check against
   * @returns boolean indicating if the user has all of the specified roles
   */
  const hasAllRoles = (roles: Role[]): boolean => {
    if (!user) return false;
    
    // Access roles from the user object
    const userRoles: string[] = user.roles || [];
    
    return roles.every(role => userRoles.includes(role));
  };

  /**
   * Check if the current user is the specified employee or has admin/HR role
   * @param employeeId - The employee ID to check against
   * @returns boolean indicating if user has permission to access employee data
   */
  const canAccessEmployeeData = (employeeId: number): boolean => {
    if (!user) return false;
    
    // Admin and HR can access any employee data
    if (hasAnyRole(['admin', 'hr', 'payroll'])) return true;
    
    // Employee can only access their own data
    return user.employeeId === employeeId;
  };

  return {
    hasAnyRole,
    hasAllRoles,
    canAccessEmployeeData,
    userRoles: user?.roles || [],
    isAuthenticated: !!user
  };
};
